/**
 * Transfer-out initiation UI (NOS-169) — sponsor-org owners only. Initiates,
 * shows the pending state with the accept link, and cancels.
 */
import { useEffect, useState } from "react"
import { ArrowRightLeft, Copy } from "lucide-react"
import { toast } from "sonner"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { supabase } from "@/lib/supabase"
import { useAuthStore } from "@/stores/authStore"
import { useOrgStore } from "@/stores/orgStore"
import type { SponsorshipWithChild } from "@/lib/sponsor"

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

interface ChildMember {
  user_id: string
  role: string
  email: string | null
}

async function invokeTransfer(body: Record<string, unknown>) {
  const { data: sess } = await supabase.auth.getSession()
  return supabase.functions.invoke("sponsor-transfer", {
    body,
    ...(sess.session?.access_token
      ? { headers: { Authorization: `Bearer ${sess.session.access_token}` } }
      : {}),
  })
}

export function TransferOutSection({
  sponsorship,
  onChanged,
}: {
  sponsorship: SponsorshipWithChild
  onChanged: () => void
}) {
  const { user } = useAuthStore()
  const { currentOrg } = useOrgStore()
  const [isOwner, setIsOwner] = useState(false)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [email, setEmail] = useState("")
  const [postMode, setPostMode] = useState<"exit" | "affiliated">("exit")
  const [members, setMembers] = useState<ChildMember[]>([])
  const [retained, setRetained] = useState<Set<string>>(new Set())
  const [formError, setFormError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [acceptUrl, setAcceptUrl] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    async function checkOwner() {
      if (!currentOrg?.id || !user?.id) return
      const { data } = await supabase
        .from("organization_members")
        .select("role")
        .eq("org_id", currentOrg.id)
        .eq("user_id", user.id)
        .maybeSingle()
      if (!cancelled) setIsOwner(data?.role === "owner")
    }
    checkOwner()
    return () => {
      cancelled = true
    }
  }, [currentOrg?.id, user?.id])

  const openDialog = async () => {
    setDialogOpen(true)
    const { data, error } = await invokeTransfer({
      action: "members",
      sponsorship_id: sponsorship.id,
    })
    if (!error && data?.members) {
      const list = data.members as ChildMember[]
      setMembers(list)
      setRetained(new Set(list.map((m) => m.user_id)))
    }
  }

  const handleInitiate = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!EMAIL_REGEX.test(email.trim())) {
      setFormError("Enter the new owner's email address")
      return
    }
    setFormError(null)
    setBusy(true)
    const removals = members.filter((m) => !retained.has(m.user_id)).map((m) => m.user_id)
    const { data, error } = await invokeTransfer({
      action: "initiate",
      sponsorship_id: sponsorship.id,
      new_owner_email: email.trim(),
      post_mode: postMode,
      member_removals: removals,
      origin: window.location.origin,
    })
    setBusy(false)
    if (error || !data?.ok) {
      setFormError(error?.message ?? "Failed to initiate transfer")
      return
    }
    setAcceptUrl(data.accept_url as string)
    toast.success(
      data.email_sent
        ? `Transfer invitation sent to ${email.trim()}`
        : "Transfer created — the invitation email failed, share the link below manually."
    )
    onChanged()
  }

  const handleCancel = async () => {
    setBusy(true)
    const { error } = await invokeTransfer({
      action: "cancel",
      sponsorship_id: sponsorship.id,
    })
    setBusy(false)
    if (error) {
      toast.error("Failed to cancel transfer")
      return
    }
    toast.success("Transfer cancelled — sponsorship is active again")
    onChanged()
  }

  if (!isOwner) return null

  if (sponsorship.status === "transferring") {
    return (
      <section className="rounded-lg border border-amber-200 bg-amber-50 p-4">
        <h2 className="font-semibold text-amber-900">Transfer pending</h2>
        <p className="mt-1 text-sm text-amber-800">
          Ownership of {sponsorship.child.name} has been offered and awaits acceptance. Donations
          continue routing to your organization until the new owner accepts.
        </p>
        <Button
          variant="outline"
          size="sm"
          className="mt-3"
          onClick={handleCancel}
          disabled={busy}
        >
          Cancel transfer
        </Button>
      </section>
    )
  }

  if (sponsorship.status !== "active") return null

  return (
    <section className="rounded-lg border p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="font-semibold">Transfer out</h2>
          <p className="text-sm text-muted-foreground">
            Hand {sponsorship.child.name} to its own leadership — its site, donors, and history
            stay in place.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={openDialog}>
          <ArrowRightLeft className="mr-2 h-4 w-4" aria-hidden />
          Start transfer
        </Button>
      </div>

      <Dialog open={dialogOpen} onOpenChange={(open) => !busy && setDialogOpen(open)}>
        <DialogContent className="sm:max-w-md">
          {acceptUrl ? (
            <>
              <DialogHeader>
                <DialogTitle>Transfer initiated</DialogTitle>
                <DialogDescription>
                  Share this link with the new owner if needed — it's single-use and expires in 30
                  days.
                </DialogDescription>
              </DialogHeader>
              <div className="flex items-center gap-2">
                <Input readOnly value={acceptUrl} className="font-mono text-xs" />
                <Button
                  type="button"
                  variant="outline"
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(acceptUrl)
                      toast.success("Link copied")
                    } catch {
                      toast.error("Failed to copy link")
                    }
                  }}
                >
                  <Copy className="h-4 w-4" aria-hidden />
                </Button>
              </div>
              <DialogFooter>
                <Button onClick={() => setDialogOpen(false)}>Done</Button>
              </DialogFooter>
            </>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle>Transfer {sponsorship.child.name}</DialogTitle>
                <DialogDescription>
                  The new owner takes full control when they accept. This cannot be undone after
                  acceptance.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleInitiate}>
                <div className="space-y-4 py-2">
                  <div className="space-y-2">
                    <Label htmlFor="transfer-email">New owner's email</Label>
                    <Input
                      id="transfer-email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="leader@example.org"
                      disabled={busy}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>After the transfer</Label>
                    <label className="flex items-start gap-2 text-sm">
                      <input
                        type="radio"
                        name="post-mode"
                        checked={postMode === "exit"}
                        onChange={() => setPostMode("exit")}
                        disabled={busy}
                        className="mt-1"
                      />
                      <span>
                        <span className="font-medium">Full exit</span> — your organization loses
                        live access, keeping a read-only archive of the sponsored period.
                      </span>
                    </label>
                    <label className="flex items-start gap-2 text-sm">
                      <input
                        type="radio"
                        name="post-mode"
                        checked={postMode === "affiliated"}
                        onChange={() => setPostMode("affiliated")}
                        disabled={busy}
                        className="mt-1"
                      />
                      <span>
                        <span className="font-medium">Stay affiliated</span> — you keep read-only
                        visibility; money and receipting move to the new owner.
                      </span>
                    </label>
                  </div>
                  {members.length > 0 && (
                    <div className="space-y-2">
                      <Label>Team members who keep access</Label>
                      {members.map((m) => (
                        <label key={m.user_id} className="flex items-center gap-2 text-sm">
                          <Checkbox
                            checked={retained.has(m.user_id)}
                            onCheckedChange={(checked) => {
                              setRetained((prev) => {
                                const next = new Set(prev)
                                if (checked) next.add(m.user_id)
                                else next.delete(m.user_id)
                                return next
                              })
                            }}
                            disabled={busy}
                          />
                          <span>
                            {m.email ?? m.user_id.slice(0, 8)}{" "}
                            <span className="text-muted-foreground">({m.role})</span>
                          </span>
                        </label>
                      ))}
                    </div>
                  )}
                  <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                    Donations keep routing to your organization until the new owner accepts. After
                    acceptance they connect their own Stripe account and EIN — this step is
                    irreversible.
                  </p>
                  {formError && <p className="text-sm text-destructive">{formError}</p>}
                </div>
                <DialogFooter>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setDialogOpen(false)}
                    disabled={busy}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" disabled={busy}>
                    {busy ? "Starting..." : "Start transfer"}
                  </Button>
                </DialogFooter>
              </form>
            </>
          )}
        </DialogContent>
      </Dialog>
    </section>
  )
}
